import { useState } from 'react';
import { Presentation, FileText, LayoutGrid, Loader2, Sparkles } from 'lucide-react';
import { generateGammaDocument, downloadFromUrl, getFormatLabel, GammaFormat } from '@/services/gammaService';
import { downloadResumenAsWord } from '@/services/resumenService';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useEffectiveMode } from '@/hooks/useEffectiveMode';
import { saveDownload, DownloadType, DownloadMode, getDownloadModeFromCuerpo } from '@/services/downloadHistoryService';
import { checkOpositorQuota, invalidateOpositorQuotaCache } from '@/services/opositorQuotaService';

interface GammaAutoSuggestProps {
  content: string;
  detectedFormat: GammaFormat;
  userQuestion?: string;
  chatTitle?: string;
}

const FORMAT_ICONS: Record<GammaFormat, typeof Presentation> = {
  presentation: Presentation,
  resumen: FileText,
  infografia: LayoutGrid,
};

/**
 * Detects Gamma intent from a user message.
 * Returns the format if detected, null otherwise.
 */
export function detectGammaIntent(userMessage: string): GammaFormat | null {
  if (!userMessage || typeof userMessage !== 'string') return null;
  const lower = userMessage.toLowerCase();
  if (/infograf[ií]a|infogr[aá]fico/.test(lower)) return 'infografia';
  if (/presentaci[oó]n|slides?|diapositivas?/.test(lower)) return 'presentation';
  if (/\bresumen\b|\besquema\b|material de estudio/.test(lower)) return 'resumen';
  return null;
}

export function GammaAutoSuggest({ content, detectedFormat, userQuestion, chatTitle }: GammaAutoSuggestProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const { toast } = useToast();
  const { user, profile } = useAuth();
  const mode = useEffectiveMode();

  const Icon = FORMAT_ICONS[detectedFormat];
  const label = getFormatLabel(detectedFormat);
  const isResumenLocal = detectedFormat === 'resumen';

  const OPOSITOR_MODES = ['oposiciones','oposiciones_c1','oposiciones_c2','oposiciones_iipp','oposiciones_guardia_civil','oposiciones_policia_nacional','oposiciones_generico'] as const;
  const isOpositor = OPOSITOR_MODES.includes(mode as any);
  let downloadMode: DownloadMode = isOpositor ? (mode as DownloadMode) : 'funcionarios';
  if (downloadMode === 'oposiciones' && profile?.opositor_body) {
    downloadMode = getDownloadModeFromCuerpo(profile.opositor_body);
  } else if (downloadMode === 'funcionarios' && profile?.opositor_body) {
    // Si genera material visual de oposiciones desde chat de funcionarios, guardarlo en su oposicion
    downloadMode = getDownloadModeFromCuerpo(profile.opositor_body);
  }

  const handleGenerate = async () => {
    if (isGenerating) return;

    // Plan free (opositor): limitar materiales del mes
    if (user && isOpositor) {
      const quota = await checkOpositorQuota(user.id, (profile as any)?.plan, profile, 'materials');
      if (!quota.allowed) {
        toast({
          title: 'Límite mensual alcanzado',
          description: `Has generado ${quota.used}/${quota.limit} materiales este mes en el plan gratuito. Vuelve el día 1 o mejora a Plan Opositor.`,
          variant: 'destructive',
        });
        return;
      }
    }

    setIsGenerating(true);

    if (isResumenLocal) {
      // ===== LOCAL RESUMEN FLOW =====
      toast({
        title: 'Generando resumen...',
        description: 'Esto puede tardar entre 10 y 20 segundos.',
      });

      try {
        const topicQuery = chatTitle || userQuestion;
        await downloadResumenAsWord({ content, topicQuery, userQuestion });
        if (user) {
          saveDownload({ userId: user.id, title: 'Resumen', downloadType: 'resumen', mode: downloadMode, metadata: { content } });
          invalidateOpositorQuotaCache();
        }
        toast({
          title: 'Resumen generado ✓',
          description: 'El archivo Word se ha descargado correctamente.',
        });
      } catch (error: any) {
        console.error('Error generating resumen:', error);
        toast({
          title: 'Error al generar resumen',
          description: error.message || 'No se pudo generar el resumen',
          variant: 'destructive',
        });
      } finally {
        setIsGenerating(false);
      }
      return;
    }

    // ===== GAMMA FLOW =====
    toast({
      title: `Generando ${label}...`,
      description: 'Esto puede tardar entre 30 y 60 segundos.',
    });

    try {
      const topicQuery = chatTitle || userQuestion;
      const result = await generateGammaDocument({ content, format: detectedFormat, topicQuery, userQuestion });

      if (result.exportUrl) {
        downloadFromUrl(result.exportUrl, `${label}.pdf`);
        const dlType: DownloadType = detectedFormat === 'infografia' ? 'infografia' : 'slides';
        if (user) {
          await saveDownload({ userId: user.id, title: label, downloadType: dlType, mode: downloadMode, fileUrl: result.exportUrl, metadata: { export_url: result.exportUrl, gamma_url: result.gammaUrl } });
          invalidateOpositorQuotaCache();
        }
        toast({
          title: `${label} generada ✓`,
          description: result.credits
            ? `PDF descargado. Créditos Gamma restantes: ${result.credits.remaining}`
            : 'El PDF se ha descargado correctamente.',
        });
      } else if (result.gammaUrl) {
        window.open(result.gammaUrl, '_blank');
        const dlType: DownloadType = detectedFormat === 'infografia' ? 'infografia' : 'slides';
        if (user) {
          await saveDownload({ userId: user.id, title: label, downloadType: dlType, mode: downloadMode, fileUrl: result.gammaUrl, metadata: { gamma_url: result.gammaUrl } });
          invalidateOpositorQuotaCache();
        }
        toast({
          title: `${label} generada ✓`,
          description: 'Se ha abierto en Gamma.',
        });
      }
    } catch (error: any) {
      console.error('Error generating gamma:', error);
      toast({
        title: 'Error al generar',
        description: error.message || `No se pudo generar la ${label.toLowerCase()}`,
        variant: 'destructive',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="mt-3 rounded-lg border border-violet-200 bg-violet-50/80 dark:border-violet-800 dark:bg-violet-950/30 p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="h-4 w-4 text-violet-500 flex-shrink-0" />
          <p className="text-sm text-violet-700 dark:text-violet-300 truncate">
            Generar <strong>{label}</strong> con este contenido
          </p>
        </div>
        <button
          onClick={handleGenerate}
          disabled={isGenerating}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap
            bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-60 disabled:cursor-not-allowed
            dark:bg-violet-700 dark:hover:bg-violet-600"
        >
          {isGenerating ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Generando...
            </>
          ) : (
            <>
              <Icon className="h-3.5 w-3.5" />
              Generar {label}
            </>
          )}
        </button>
      </div>
    </div>
  );
}
